// ── buildUpdateStore — "a newer frontend has deployed" for the open tab ──
//
// A trader can hold a tab open for a whole slate. When a new frontend ships,
// nothing about the running page changes, so without a signal the tab quietly
// keeps serving a stale build. This store polls the deploy's published build
// identity and raises a non-blocking prompt. Adoption is ALWAYS an explicit
// user click: `reloadNow` is the only reload this feature can cause, and nothing
// calls it automatically. (The app's other reload sites — ErrorBoundary's crash
// screen and the nav-timeout retry — are likewise explicit user clicks, so no
// automatic reload path exists anywhere.) A surprise reload mid-order is worse
// than a stale build.
//
// One poller, many subscribers: `startBuildWatch` is refcounted, so the rail
// pip, the status-bar item, and the corner breakout all read the same state
// without any of them opening a second timer or a second request.
//
// ## The two payload shapes
//
// `/version.json` is written by two different producers, and they disagree:
//
//   g1.pm / dev.g1.pm / g1.dan   {"app","env","commit","deployed"}   ← box post-receive hook
//   g1.gbuff.com (real prod)     {"version","deployedAt","branch"}   ← vite.config.ts plugin
//
// The pruned predecessor (deleted in 51994443) read only `.version` and bailed
// when it was absent, which made it permanently silent on three of the four
// lanes. `readBuildIdentity` accepts either key for that reason.
//
// Identity is the ONLY change signal; both timestamps are ignored on purpose.
// Rebuilding the same commit re-stamps `deployed`/`deployedAt` without changing
// what the browser would download, and nagging a trader to reload into the
// bytes they are already running is a false alarm.

import { create } from 'zustand';
import { releaseBody } from '@/lib/infra/responseBody';

/** Poll cadence. Inherited from the pruned hook — a deploy is not urgent. */
const POLL_INTERVAL_MS = 60_000;

/**
 * Floor between two checks from ANY trigger. Focus and visibility events fire
 * in bursts while alt-tabbing; without this, a flurry of tab switches becomes a
 * flurry of requests. The interval (60s) is comfortably above it, so the steady
 * cadence is never throttled.
 */
const MIN_CHECK_GAP_MS = 10_000;

/**
 * Hard deadline on a single probe, covering a stalled connect AND a stalled
 * body read. Without it a request that never settles would leave `inFlight`
 * true forever and every later check would bail at the guard — one bad request
 * silently killing the watch for the rest of the tab's life, which is the exact
 * staleness this feature exists to prevent. An abort is just another quiet
 * failure; the next tick retries.
 */
const REQUEST_TIMEOUT_MS = 15_000;

/** Respects a non-root deploy base, same as the pruned hook did. */
const VERSION_URL = `${import.meta.env.BASE_URL}version.json`;

/**
 * The build identity out of either producer's payload, or null if this isn't a
 * usable `version.json`. Null means "learned nothing" — never "error".
 */
export function readBuildIdentity(payload: unknown): string | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const { commit, version } = payload as { commit?: unknown; version?: unknown };
  // `commit` first: three of the four lanes emit it, and a payload carrying
  // both should prefer the one the deploy hook wrote last.
  const raw = typeof commit === 'string' && commit.trim() !== '' ? commit : version;
  if (typeof raw !== 'string') return null;
  const identity = raw.trim();
  return identity === '' ? null : identity;
}

interface BuildUpdateState {
  /** The build this tab is running — the first identity it ever observed. */
  baseline: string | null;
  /** The most recent identity observed, whether or not it differs. */
  latest: string | null;
  /** A different build is live. Stays true until the tab reloads into it. */
  updateAvailable: boolean;
  /** Whether the corner breakout specifically has been waved off. */
  breakoutDismissed: boolean;
  observe: (identity: string) => void;
  dismissBreakout: () => void;
  showBreakout: () => void;
  reloadNow: () => void;
}

export const useBuildUpdateStore = create<BuildUpdateState>((set) => ({
  baseline: null,
  latest: null,
  updateAvailable: false,
  breakoutDismissed: false,

  observe: (identity) =>
    set((state) => {
      // First successful read is the baseline, never an update: the tab is by
      // definition already running whatever it just fetched.
      if (state.baseline === null) return { baseline: identity, latest: identity };

      // Unchanged since the last look — in particular, leave a dismissal alone.
      if (identity === state.latest) return {};

      // Rolled back to the build this tab is running. There is nothing left to
      // adopt, so retract the prompt instead of asking for a pointless reload.
      if (identity === state.baseline) {
        return { latest: identity, updateAvailable: false, breakoutDismissed: false };
      }

      // A genuinely new build re-arms the breakout: dismissing one release must
      // not silence every release after it.
      return { latest: identity, updateAvailable: true, breakoutDismissed: false };
    }),

  dismissBreakout: () => set({ breakoutDismissed: true }),
  showBreakout: () => set({ breakoutDismissed: false }),

  /** This feature's only reload. Explicit user action, never automatic. */
  reloadNow: () => window.location.reload(),
}));

/**
 * Test seam — restores a fresh tab: observed state AND any watch left running.
 * The refcount is module state, so a case that throws before disposing would
 * otherwise leak a live poller into every case after it.
 */
export function resetBuildUpdateStore(): void {
  stopActiveWatch?.();
  stopActiveWatch = null;
  activeWatchers = 0;
  useBuildUpdateStore.setState({
    baseline: null,
    latest: null,
    updateAvailable: false,
    breakoutDismissed: false,
  });
}

function beginWatch(): () => void {
  let inFlight = false;
  let lastCheckAt = 0;
  let disposed = false;

  const check = async (): Promise<void> => {
    if (disposed || inFlight) return;
    const now = Date.now();
    if (lastCheckAt !== 0 && now - lastCheckAt < MIN_CHECK_GAP_MS) return;
    inFlight = true;
    lastCheckAt = now;
    // AbortController + setTimeout rather than AbortSignal.timeout: same
    // deadline, but built from primitives a fake clock can drive, so the wedge
    // this guards against is actually reachable in a test.
    const controller = new AbortController();
    const deadline = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      const resp = await fetch(VERSION_URL, { cache: 'no-store', signal: controller.signal });
      if (!resp.ok) { releaseBody(resp); return; }
      const identity = readBuildIdentity(await resp.json());
      if (identity !== null && !disposed) useBuildUpdateStore.getState().observe(identity);
    } catch {
      // Unreachable, non-JSON, or mid-deploy half-written file. A build-identity
      // probe must never degrade the app it is watching, so every failure is a
      // no-op: no error state, no retry storm, no UI change. The dev server
      // serves no version.json at all (the vite plugin is build-only), which
      // lands here as a plain 404 and is correct.
    } finally {
      clearTimeout(deadline);
      inFlight = false;
    }
  };

  // Only when actually visible: a background tab firing `focus` has nothing to
  // show the user, and the next interval tick covers it anyway.
  const onWake = (): void => {
    if (document.visibilityState === 'visible') void check();
  };

  void check();
  // Created exactly once and never reset. Wake events call the same `check`
  // rather than restarting the timer, so no trigger can duplicate the cadence.
  const timer = setInterval(() => void check(), POLL_INTERVAL_MS);
  document.addEventListener('visibilitychange', onWake);
  window.addEventListener('focus', onWake);

  return () => {
    disposed = true;
    clearInterval(timer);
    document.removeEventListener('visibilitychange', onWake);
    window.removeEventListener('focus', onWake);
  };
}

let activeWatchers = 0;
let stopActiveWatch: (() => void) | null = null;

/**
 * Start watching for a newer build; returns a disposer. Refcounted and
 * idempotent, so extra callers (or a StrictMode remount) share the single
 * poller instead of adding one, and a disposer called twice is harmless.
 */
export function startBuildWatch(): () => void {
  activeWatchers += 1;
  if (activeWatchers === 1) stopActiveWatch = beginWatch();

  let released = false;
  return () => {
    if (released) return;
    released = true;
    activeWatchers -= 1;
    if (activeWatchers === 0) {
      stopActiveWatch?.();
      stopActiveWatch = null;
    }
  };
}
